import  { useContext, useState, useEffect, useMemo } from 'react'
import { Context } from '@entities/Context'
import dayjs from 'dayjs'
import {isEmpty} from 'lodash'

export default function PayoutReport({ onError }) {

    const { storage } = useContext(Context)
    const [open, setOpen] = useState(false)
    const [selected, setSelected] = useState(0)
    const [customStart, setCustomStart] = useState(null)
    const [customEnd, setCustomEnd] = useState(null)
    const [account, setAccount] = useState({})

    const presets = [
        'Last 7 days',
        'Last 14 days',
        'Last Month',
        'Custom'
    ]

    const maxEnd = dayjs().format('YYYY-MM-DD')
    const maxStart = dayjs().subtract(1, 'day').format('YYYY-MM-DD')

    // Computed values as memoized values
    const isCustom = useMemo(() => presets[selected] === 'Custom', [selected, presets])

    const start = useMemo(() => {
        if (isCustom) {
            return dayjs(customStart)
        }

        const today = dayjs()
        switch (presets[selected]) {
            case 'Last 7 days':
                return today.subtract(7, 'days')
            case 'Last 14 days':
                return today.subtract(14, 'days')
            case 'Last Month':
                return today.subtract(1, 'month')
            default:
                return today
        }
    }, [isCustom, customStart, selected, presets])

    const end = useMemo(() => {
        if (isCustom) {
            return dayjs(customEnd)
        }
        return dayjs()
    }, [isCustom, customEnd])

    const canSubmit = useMemo(() => {
        if (isCustom) {
            return !isEmpty(customStart) && !isEmpty(customEnd)
        }
        return true
    }, [isCustom, customStart, customEnd])

    // Effects to handle mounted/initialization
    useEffect(() => {
        const acc = storage.get('account')
        setAccount(acc || {})

        setCustomStart(start.format('YYYY-MM-DD'))
        setCustomEnd(end.format('YYYY-MM-DD'))
    }, [])

    // Event handlers
    const toggle = () => setOpen(!open)

    const select = (index) => {
        setSelected(index)
        // Update custom dates when preset changes
        const newStart = index === presets.indexOf('Custom') ? customStart : dayjs().subtract(
            index === 0 ? 7 : index === 1 ? 14 : 30,
            index === 2 ? 'month' : 'days'
        ).format('YYYY-MM-DD')
        const newEnd = dayjs().format('YYYY-MM-DD')
        setCustomStart(newStart)
        setCustomEnd(newEnd)
    }

    const submit = () => {
        const url = new URL(window.location.origin + '/api/payout/report')
        url.searchParams.append('email', account.email)
        url.searchParams.append('start', start.format('YYYY-MM-DD'))
        url.searchParams.append('end', end.format('YYYY-MM-DD'))
        url.searchParams.append('pin', storage.get('pin'))
        window.open(url.toString(), '_blank')
    }

    return (
        <div className="relative">
            <button
                onClick={toggle}
                className="inline-block px-4 py-2 mx-auto my-4 text-sm font-bold text-white rounded bg-primary"
                type="button"
            >
                New Report
            </button>
            {open && (
                <div className="absolute right-0 z-10 flex transition-opacity bg-white rounded shadow-md w-fit shadow-primary">
                    <div className="flex flex-col justify-between flex-grow border-r-2 w-fit border-r-gray-300">
                        {presets.map((preset, idx) => (
                            <button
                                key={preset}
                                onClick={() => select(idx)}
                                className={`w-full h-full px-4 text-sm font-bold text-left whitespace-no-wrap hover:bg-primary hover:text-white
                                    ${selected === idx ? 'bg-primary text-white' : ''}
                                    ${idx === 0 ? 'rounded-tl' : ''}
                                    ${idx === presets.length - 1 ? 'rounded-bl' : ''}`}
                            >
                                {preset}
                            </button>
                        ))}
                    </div>

                    <div className="flex flex-col flex-grow p-4">
                        <div className="flex flex-col p-2">
                            <label htmlFor="start" className="text-sm font-bold text-primary">
                                Start
                            </label>
                            <input
                                name="start"
                                type="date"
                                value={customStart || ''}
                                onChange={(e) => setCustomStart(e.target.value)}
                                onFocus={() => setSelected(presets.indexOf('Custom'))}
                                className="p-2 border-2 rounded border-primary disabled:cursor-not-allowed disabled:bg-gray-300"
                            />
                        </div>

                        <div className="flex flex-col p-2 mb-1">
                            <label htmlFor="end" className="text-sm font-bold text-primary">
                                End
                            </label>
                            <input
                                name="end"
                                type="date"
                                max={maxEnd}
                                value={customEnd || ''}
                                onChange={(e) => setCustomEnd(e.target.value)}
                                onFocus={() => setSelected(presets.indexOf('Custom'))}
                                className="p-2 border-2 rounded border-primary disabled:cursor-not-allowed disabled:bg-gray-300"
                            />
                        </div>

                        <button
                            onClick={submit}
                            className="px-6 py-3 m-2 text-sm font-bold text-white whitespace-no-wrap rounded w-fit bg-primary disabled:bg-blue-400 disabled:cursor-not-allowed"
                            type="button"
                            disabled={!canSubmit}
                        >
                            Download
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}
